import React from 'react';

import {
  Box,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
  gridItem: { flexGrow: 1 },
  mb5: { marginBottom: 5 },
  mb20: { marginBottom: 20 },
  mt20: { marginTop: 20 },
  mb25: { marginBottom: 25 },
  mr20: { marginRight: 20 },
  h100: { height: '100%' },
  container: { padding: 20, marginBottom: 20 },
  divider: { marginTop: 30, marginBottom: 20 },
  tableContainer: { minHeight: '380px', height: '100%' },
}));

interface Props {
  page?: 'list' | 'history' | 'manage';
}

const AdvanceRequestSkeleton = ({ page = 'list' }: Props): JSX.Element => {
  const classes = useStyles();

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      className={classes.h100}
    >
      <Box mb={4}>
        <Skeleton variant="text" animation="wave" height={30} width={420} />
        <Skeleton variant="text" animation="wave" height={20} width={270} />
      </Box>
      {page === 'history' || page === 'manage' ? (
        <Paper className={classes.container}>
          {page === 'history' && (
            <>
              <Grid container direction="row" justifyContent="space-between">
                <Box flex={1} marginX={3}>
                  <Skeleton
                    variant="rectangular"
                    height={55}
                    width="100%"
                    className={classes.mb25}
                  />
                  <Skeleton variant="rectangular" height={55} width="100%" />
                </Box>
                <Divider flexItem orientation="vertical" />
                <Box flex={1} marginX={3}>
                  <Skeleton variant="rectangular" height={55} width="100%" />
                </Box>
                <Divider flexItem orientation="vertical" />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection="column"
                  flex={1}
                  marginX={3}
                >
                  <Skeleton
                    variant="text"
                    height={22}
                    width={130}
                    className={classes.mb5}
                  />
                  <Skeleton
                    variant="text"
                    height={22}
                    width={140}
                    className={classes.mb5}
                  />
                  <Skeleton
                    variant="text"
                    height={22}
                    width={125}
                    className={classes.mb5}
                  />
                  <Skeleton
                    variant="text"
                    height={22}
                    width={120}
                    className={classes.mb5}
                  />
                  <Skeleton variant="text" height={22} width={120} />
                </Box>
              </Grid>
              <Divider className={classes.divider} />
              <Box display="flex" flex={1} justifyContent="center">
                <Skeleton variant="rectangular" height={35} width={125} />
              </Box>
            </>
          )}
          {page === 'manage' && (
            <>
              <Skeleton variant="text" height={20} />
              <Skeleton variant="text" height={70} />
              <Divider className={classes.divider} />
              <Grid container direction="row">
                <Skeleton
                  variant="rectangular"
                  height={35}
                  width={370}
                  className={classes.mr20}
                />
                <Skeleton
                  variant="rectangular"
                  height={35}
                  width={225}
                  className={classes.mr20}
                />
                <Skeleton variant="rectangular" height={35} width={100} />
              </Grid>
            </>
          )}
        </Paper>
      ) : (
        <></>
      )}

      <Grid
        className={classes.mb20}
        container
        direction="row"
        justifyContent={page === 'list' ? 'space-between' : 'flex-end'}
      >
        {page === 'list' && (
          <Skeleton variant="rectangular" height={35} width={450} />
        )}
        <Skeleton variant="rectangular" height={35} width={410} />
      </Grid>

      <Box>
        <Grid item className={classes.gridItem}>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="right">
                    <Skeleton variant="rectangular" />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="rectangular" />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="rectangular" />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="rectangular" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 8 }, (_, i) => i + 1).map((i) => (
                  <TableRow key={i}>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Box>

      <Grid
        className={classes.mt20}
        direction="row"
        container
        justifyContent={page === 'list' ? 'space-between' : 'flex-end'}
      >
        {page === 'list' && (
          <Skeleton variant="rectangular" height={35} width={450} />
        )}
        <Skeleton variant="rectangular" height={35} width={410} />
      </Grid>
    </Grid>
  );
};

export default AdvanceRequestSkeleton;
